import { useState } from "react";
import { toast } from "react-toastify";
import { BasicDetailsModal } from "./BasicDetailsModal";
import { newAuthService } from "Service/Axios";
import { ImageCropper } from "Components/Modal";
import {
  DEFAULT_MODULE_TYPE,
  IMAGE_TYPES,
  VIEW_PROFILE_SECTIONS,
} from "Constant/constants";
import {
  formatDateWithFullMonth,
  formatMonthYear,
  formatYear,
  uploadHandler,
} from "Utils";
import { useTranslation } from "react-i18next";
import { saveUserProfileUrl } from "Constant/api";

export const BasicDetails = ({
  userProfileDetails,
  fetchData,
  isCurrentUserProfile,
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [basicDetailsModal, setBasicDetailsModal] = useState(false);
  const [imageCropModal, setImageCropModal] = useState(false);
  const [imageCropPath, setCropImagePath] = useState("");
  const [imageType, setImageType] = useState("");
  const [imageFile, setImageFile] = useState(null);

  // Functions to toggle modals
  const toggleBasicDetailsModal = () => {
    setBasicDetailsModal((prev) => !prev);
  };

  const toggleImageCropModal = (type = "") => {
    setImageType(type);
    setImageCropModal(!imageCropModal);
  };

  // Functions to change profile or cover picture
  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageFile(file);
        setCropImagePath(reader.result);
        toggleImageCropModal(type);
      };
      reader.readAsDataURL(file);
    }
  };

  // Function to save profile picture
  const onSaveProfilePicture = async (e, cropFile) => {
    setLoading(true);
    const mockEvent = {
      target: {
        files: [cropFile],
      },
    };
    const moduleType =
      imageType === "profile"
        ? DEFAULT_MODULE_TYPE.profilePicture
        : DEFAULT_MODULE_TYPE.coverPicture;
    const response = await uploadHandler(mockEvent, moduleType, IMAGE_TYPES);
    if (response && response.url) {
      onUserInfoUpdate(response.url);
    } else {
      setLoading(false);
    }
  };

  const onUserInfoUpdate = async (res) => {
    try {
      const payload = {};
      if (imageType === "profile") {
        payload.key = VIEW_PROFILE_SECTIONS.photoURL;
        payload[VIEW_PROFILE_SECTIONS.photoURL] = res;
      }
      if (imageType === "cover") {
        payload.key = VIEW_PROFILE_SECTIONS.coverimageURL;
        payload[VIEW_PROFILE_SECTIONS.coverimageURL] = res;
      }
      setLoading(true);
      const response = await newAuthService(
        saveUserProfileUrl,
        "POST",
        payload
      );
      if (response.status) {
        toast.success(t("common.messages.notification.success.profileUpdated"));
        fetchData();
      } else {
        toast.error(response.message);
      }
    } catch {
      toast.error(t("common.messages.notification.failed.somethingWentWrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="rounded-lg border border-grey-200 bg-white">
        <div className="position-relative">
          <div className="cover-img-box w-100 h-32 rounded-xl overflow-hidden">
            <img
              src={
                userProfileDetails?.coverimageURL ||
                "/assets/images/placeholders/cover-image-placeholder.svg"
              }
              alt="user-cover-image"
              className="w-100 h-100 object-fit-cover"
            />
          </div>
          {isCurrentUserProfile && (
            <div className="upload-btn-wrapper position-absolute right-2 bottom-2 w-8 h-8 cursor-pointer">
              <button
                type="button"
                className="btn btn-light font-medium rounded-lg d-inline-flex justify-content-center align-items-center gap-1 p-0 border-transparent w-100 h-100"
                disabled={loading}
              >
                <i className="bx bx-camera text-lg"></i>
                <input
                  type="file"
                  className="h-auto"
                  onChange={(e) => handleImageChange(e, "cover")}
                  disabled={loading}
                  accept={IMAGE_TYPES}
                />
              </button>
            </div>
          )}
        </div>
        <div className="p-4 d-flex flex-column row-gap-2 -mt-14 w-100">
          <div className="d-flex align-items-end gap-3">
            <div className="w-28 h-28 position-relative">
              <div className="profile-user-img w-28 h-28 rounded-circle border border-4 border-yellow-500 overflow-hidden">
                <img
                  src={
                    userProfileDetails?.photoURL ||
                    "/assets/images/placeholders/profile-image-placeholder.svg"
                  }
                  alt="user-profile-image"
                  className="w-100 h-100 object-fit-cover"
                />
              </div>
              {isCurrentUserProfile && (
                <div className="upload-btn-wrapper position-absolute right-2 bottom-2 w-8 h-8 cursor-pointer">
                  <button
                    type="button"
                    className="btn btn-light font-medium rounded-lg d-inline-flex justify-content-center align-items-center gap-1 p-0 border-transparent w-100 h-100"
                    disabled={loading}
                  >
                    <i className="bx bx-camera text-lg"></i>
                    <input
                      type="file"
                      className="h-auto"
                      onChange={(e) => handleImageChange(e, "profile")}
                      disabled={loading}
                    />
                  </button>
                </div>
              )}
            </div>
            <div className="ms-auto">
              <ul className="list-unstyled mb-0 d-flex flex-wrap align-items-center gap-2">
                {/* <li>
                  <button
                    type="button"
                    className="btn btn-primary rounded-lg d-inline-flex align-items-center gap-1"
                  >
                    <i className="bx bx-envelope"></i> Email
                  </button>
                </li> */}

                {isCurrentUserProfile && (
                  <li>
                    <button
                      type="button"
                      className="btn btn-gray-light rounded-lg d-inline-flex align-items-center gap-1"
                      onClick={toggleBasicDetailsModal}
                    >
                      <i className="bx bx-pencil"></i> Edit
                    </button>
                  </li>
                )}
              </ul>
            </div>
          </div>

          <div>
            <ul className="mb-0 list-unstyled d-inline-flex flex-wrap gap-2">
              <li>
                <h3 className="mb-0 text-lg font-semibold text-grey-800">
                  {userProfileDetails?.personalDetails?.firstName}{" "}
                  {userProfileDetails?.personalDetails?.lastName}
                </h3>
              </li>

              <li className="text-grey-300">|</li>
              {userProfileDetails?.education?.length > 0 && (
                <li>
                  <span>
                    {userProfileDetails?.education?.[0].institution}, Batch of{" "}
                    {formatYear(userProfileDetails?.education?.[0].endDate)}
                  </span>
                </li>
              )}
            </ul>
          </div>

          {userProfileDetails?.lastLogin && (
            <div className="last-online text-sm">
              <span className="text-grey-500 me-2">Last Online</span>{" "}
              <span className="font-medium">
                Last Online{" "}
                {formatDateWithFullMonth(userProfileDetails?.lastLogin)}
              </span>
            </div>
          )}

          <div>
            <ul className="mb-0 list-unstyled d-inline-flex flex-wrap gap-3">
              {userProfileDetails?.careerInterest?.careerGoals?.length > 0 && (
                <li>
                  <span className="d-flex align-items-center gap-1">
                    <i className="bx bx-briefcase"></i>{" "}
                    {userProfileDetails?.careerInterest?.careerGoals?.[0]}
                  </span>
                </li>
              )}
              {userProfileDetails?.personalDetails?.currentCity &&
                userProfileDetails?.personalDetails?.currentCountry && (
                  <li>
                    <span className="d-flex align-items-center gap-1">
                      <i className="bx bx-map"></i>{" "}
                      {userProfileDetails?.personalDetails?.currentCity},{" "}
                      {userProfileDetails?.personalDetails?.currentCountry}
                    </span>
                  </li>
                )}
              {userProfileDetails?.onlineProfiles?.length > 0 && (
                <li>
                  <span className="d-flex align-items-center gap-1">
                    <i className="bx bx-link-alt"></i>{" "}
                    <a
                      href={userProfileDetails?.onlineProfiles[0].profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {userProfileDetails?.onlineProfiles[0].profileUrl}
                    </a>
                  </span>
                </li>
              )}
              {userProfileDetails?.careerInterest?.availabilityDate && (
                <li>
                  <span className="d-flex align-items-center gap-1">
                    <i className="bx bx-calendar-check"></i> Available from{" "}
                    {formatMonthYear(
                      userProfileDetails?.careerInterest?.availabilityDate
                    )}
                  </span>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {basicDetailsModal && (
        <BasicDetailsModal
          show={basicDetailsModal}
          basicDetailsData={userProfileDetails?.personalDetails || null}
          toggle={toggleBasicDetailsModal}
          fetchData={fetchData}
        />
      )}

      {imageCropModal && (
        <ImageCropper
          isLoading={loading}
          show={imageCropModal}
          imageFile={imageFile}
          imageType={imageType}
          src={imageCropPath}
          aspectRatio={imageType === "profile" ? 1 / 1 : 16 / 9}
          toggle={toggleImageCropModal}
          handleCrop={onSaveProfilePicture}
        />
      )}
    </>
  );
};
